import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionCard, cn, Dialog, Text, UnsavedChangesDialog, useUnsavedChangesWarning } from '@viapeople/components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { getLogoUploadData } from '../../../utils/files'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { OrganizationForm } from '../components/OrganizationForm'
import type { OrganizationFormInput, OrganizationWithModules } from '../types'

function toFormInput(org: OrganizationWithModules): OrganizationFormInput {
	return {
		...org,
		address: org.address ?? '',
		city: org.city ?? '',
		state: org.state ?? '',
		zipcode: org.zipcode ?? '',
		country: org.country ?? '',
		enpsModuleEnabled: org.enpsModuleEnabled ?? false,
		loopModuleEnabled: org.loopModuleEnabled ?? false,
		pecModuleEnabled: org.pecModuleEnabled ?? false,
		dealTeamModuleEnabled: org.dealTeamModuleEnabled ?? false,
		caLimitedAccessEnabled: org.caLimitedAccessEnabled ?? false,
		defaultOktaPassword: null,
	}
}

export function OrganizationProfileTab() {
	const { organization } = useOutletContext<{ organization: OrganizationWithModules }>()

	const [formValues, setFormValues] = useState<OrganizationFormInput>(toFormInput(organization))
	const [isDirty, setIsDirty] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [hasNewLogo, setHasNewLogo] = useState(false)

	const queryClient = useQueryClient()

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (hasNewLogo) {
			setIsDirty(true)
			return
		}
		const referenceValues = toFormInput(organization)
		const excludeKeys = ['updatedAt', 'createdAt', 'defaultOktaPassword']
		const hasChanges = Object.keys(formValues)
			.filter((key) => !excludeKeys.includes(key))
			.some((key) => {
				const k = key as keyof OrganizationFormInput
				return referenceValues[k] !== formValues[k]
			})
		setIsDirty(hasChanges)
	}, [formValues, organization, hasNewLogo])

	const toast = useToast()

	const { mutate: updateOrganization } = useMutation(
		trpc.admin.organizations.edit.mutationOptions({
			onSuccess: (data: OrganizationWithModules) => {
				setFormValues(toFormInput(data))
				setIsDirty(false)
				toast.showToast('Organization updated')
			},
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.organizations.getById.queryKey({ id: organization.id }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const { mutate: uploadLogoRequest } = useMutation(
		trpc.admin.organizations.logoUploadRequest.mutationOptions({
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
		}),
	)

	const { mutate: clearUsers } = useMutation(
		trpc.admin.users.deleteAll.mutationOptions({
			onSuccess: () => {
				toast.showToast('Users data archived successfully')
				setIsDeleteModalOpen(false)
			},
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
			onSettled: () => {
				setIsLoading(false)
				queryClient.invalidateQueries({
					queryKey: trpc.admin.users.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const onSubmit = async (data: OrganizationFormInput, logo?: File) => {
		setIsLoading(true)
		if (!logo) {
			updateOrganization(data, {
				onSettled: () => setIsLoading(false),
			})
			return
		}

		const fileData = getLogoUploadData(organization.id, logo)

		uploadLogoRequest(fileData, {
			onSuccess: async (result) => {
				const response = await fetch(result.uploadUrl, {
					method: 'PUT',
					body: logo,
					headers: {
						'Content-Type': logo.type,
					},
				})
				if (response.ok) {
					updateOrganization(
						{ ...data, logo: result.readUrl ?? '' },
						{
							onSettled: () => {
								setIsLoading(false)
							},
						},
					)
				} else {
					toast.showToast('An error occurred. Try again later.', 'error')
					setIsLoading(false)
				}
			},
		})
	}

	const handleCancel = () => {
		setFormValues(toFormInput(organization))
		setIsDirty(false)
	}

	const handleFormChange = useCallback((data: OrganizationFormInput, hasNewLogo?: boolean) => {
		setFormValues(data)
		setHasNewLogo(hasNewLogo || false)
	}, [])

	const initialFormValues = useMemo(() => toFormInput(organization), [organization])

	const handleOpenDeleteModal = () => {
		setIsDeleteModalOpen(true)
	}

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false)
	}

	const handleDeleteConfirm = () => {
		setIsLoading(true)
		clearUsers({ organizationId: organization.id })
	}

	return (
		<div className={cn('flex flex-col items-start pb-6', isDirty && 'pb-30')}>
			<Text variant='h6' weight='semibold' className='ml-6 mb-6'>
				Profile Information
			</Text>

			<OrganizationForm
				initialValues={initialFormValues}
				onSubmit={onSubmit}
				onCancel={isDirty ? handleCancel : undefined}
				onChange={handleFormChange}
				isSubmitting={isLoading}
				submitButtonText={isLoading ? 'Saving...' : 'Save'}
				cancelButtonText='Discard Changes'
				showButtons={isDirty}
			/>
			<div className='w-full px-6'>
				<ActionCard
					title='Archive users data'
					button={{ children: 'Archive users data', onClick: handleOpenDeleteModal }}>
					Archive all users data from the organization
				</ActionCard>
			</div>

			<Dialog
				isOpen={isDeleteModalOpen}
				onClose={handleCloseDeleteModal}
				onConfirm={handleDeleteConfirm}
				title='Archive users data'
				message='You are about to archive all users data from this organization.'
				type='archive'
				confirmLabel='Archive users data'
				requireConfirmText
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</div>
	)
}
